import React, { useState } from "react";
import { Form, Input, Select, TextArea, Table, Button, Message, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { Sbti } from "../../../../api";
import { countryOptionsWithCurrent } from "../../../../utils/countries";
import "./Paso1Inventario.scss";

const sbtiController = new Sbti();

const ENFOQUE_OPTIONS = [
  { key: "market", value: "market", text: "Market-based" },
  { key: "location", value: "location", text: "Location-based" },
];

// Portado de vP1() del prototipo (línea 514) — mismos campos, mismos
// textos de ayuda y las mismas fuentes citadas al pie.
export function Paso1Inventario({ record, computed, patchSection, accessToken, company, reload }) {
  const inv = record.inventory || {};
  const params = computed?.params || {};
  const calc = computed?.calc || {};
  const currentYear = computed?.currentYear || new Date().getFullYear();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const setInv = (patch) => patchSection("inventory", { ...inv, ...patch });

  const setSite = (index, patch) => {
    const sites = [...(record.sites || [])];
    sites[index] = { ...sites[index], ...patch };
    patchSection("sites", sites, { immediate: false });
  };
  const addSite = () => patchSection("sites", [...(record.sites || []), { freeName: "", country: "", scope1: "", scope2: "" }], { immediate: true });
  const removeSite = (index) => {
    const sites = [...(record.sites || [])];
    sites.splice(index, 1);
    patchSection("sites", sites, { immediate: true });
  };
  const useSitesSum = () => {
    const sites = record.sites || [];
    const sumS1 = sites.reduce((a, s) => a + (parseFloat(String(s.scope1).replace(",", ".")) || 0), 0);
    const sumS2 = sites.reduce((a, s) => a + (parseFloat(String(s.scope2).replace(",", ".")) || 0), 0);
    const patch = { scope1: String(sumS1) };
    if (inv.scope2Approach === "market") patch.scope2Market = String(sumS2);
    else patch.scope2Location = String(sumS2);
    patchSection("inventory", { ...inv, ...patch }, { immediate: true });
    toast.success("Consolidado actualizado con la suma de sitios");
  };

  const inventoryRecent = inv.inventoryYear && currentYear - Number(inv.inventoryYear) <= (params.inventario_max_antiguedad ?? 2);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await sbtiController.syncGei(accessToken, company, "auto");
      setSyncResult(result);
      if (result.ok) {
        toast.success("Sincronizado con el módulo GEI");
        reload();
      }
    } catch (error) {
      toast.error("No se pudo sincronizar con el módulo GEI");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="sbti-paso1">
      <div className="sbti-pagehead">
        <div className="sbti-crumb">Paso 1 · Preparación</div>
        <h2>Inventario GEI del año base</h2>
        <p className="sbti-sub">
          Base de todo el proceso: inventario conforme al GHG Protocol (Corporate Standard + Scope 2 Guidance +
          Scope 3 Standard). Los totales pueden cargarse a mano o sincronizarse desde el módulo GEI de Sustemia.
        </p>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">CONECTOR</span> Sincronización con el módulo GEI</h3>
        <p className="sbti-hint">
          Trae automáticamente el inventario por año y categoría desde el módulo GEI. Mientras esa integración no
          esté resuelta del lado de la plataforma, la carga es manual acá abajo.
        </p>
        <Button primary loading={syncing} onClick={handleSync}>
          <Icon name="sync" /> Sincronizar
        </Button>
        {syncResult && !syncResult.ok && (
          <Message warning>
            <Message.Header>Sincronización no disponible todavía</Message.Header>
            <p>{syncResult.msg}</p>
          </Message>
        )}
        {syncResult && syncResult.ok && (
          <Message positive>
            <Message.Header>Sincronizado</Message.Header>
            <p>Años actualizados: {(syncResult.aniosSeguimiento || []).join(", ") || "ninguno"}</p>
          </Message>
        )}
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">CONSOLIDADO</span> Emisiones del año base — nivel empresa</h3>
        <Form>
          <Form.Group widths="equal">
            <Form.Field error={!!inv.baseYear && Number(inv.baseYear) < (params.anio_base_min ?? 2015)}>
              <label>Año base (≥ {params.anio_base_min ?? 2015})</label>
              <Input
                type="number"
                min={params.anio_base_min ?? 2015}
                max={currentYear}
                value={inv.baseYear || ""}
                onChange={(_, d) => setInv({ baseYear: d.value })}
              />
              {!!inv.baseYear && Number(inv.baseYear) < (params.anio_base_min ?? 2015) && (
                <div className="sbti-field-error">El año base tiene que ser ≥ {params.anio_base_min ?? 2015}.</div>
              )}
            </Form.Field>
            <Form.Field>
              <label>Año del inventario más reciente</label>
              <Input type="number" value={inv.inventoryYear || ""} onChange={(_, d) => setInv({ inventoryYear: d.value })} />
              <div className="sbti-hint">
                Para presentar en {currentYear}: {currentYear - 2} o {currentYear - 1} (≤ {params.inventario_max_antiguedad ?? 2} años; sin datos proxy).
              </div>
            </Form.Field>
            <Form.Field>
              <label>Enfoque Alcance 2 (único, declarado)</label>
              <Select options={ENFOQUE_OPTIONS} value={inv.scope2Approach || "market"} onChange={(_, d) => setInv({ scope2Approach: d.value })} />
              <div className="sbti-hint">Se fija UN solo enfoque para línea base y seguimiento (GHGP Scope 2 Guidance).</div>
            </Form.Field>
            <Form.Field>
              <label>Exclusiones A1+A2 (%)</label>
              <Input type="number" step="0.1" value={inv.exclusionPercent || ""} onChange={(_, d) => setInv({ exclusionPercent: d.value })} />
              <div className="sbti-hint">Máximo permitido: {params.exclusion_max ?? 5}% combinado.</div>
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Alcance 1 (tCO₂e)</label>
              <Input type="number" step="0.01" value={inv.scope1 || ""} onChange={(_, d) => setInv({ scope1: d.value })} />
              <div className="sbti-hint">Combustión fija y móvil + refrigerantes/gases F.</div>
            </Form.Field>
            <Form.Field>
              <label>Alcance 2 market-based (tCO₂e)</label>
              <Input type="number" step="0.01" value={inv.scope2Market || ""} onChange={(_, d) => setInv({ scope2Market: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Alcance 2 location-based (tCO₂e)</label>
              <Input type="number" step="0.01" value={inv.scope2Location || ""} onChange={(_, d) => setInv({ scope2Location: d.value })} />
              <div className="sbti-hint">Necesario también para el chequeo PYME (&lt; {(params.sme_tco2e ?? 10000).toLocaleString("es-AR")} tCO₂e A1+A2 location-based).</div>
            </Form.Field>
          </Form.Group>
        </Form>

        <div className="sbti-kpis">
          <div className="sbti-kpi">
            <div className="sbti-kpi__l">A1 + A2 ({inv.scope2Approach || "market"})</div>
            <div className="sbti-kpi__v">{((calc.scope1 || 0) + (calc.scope2 || 0)).toLocaleString("es-AR", { maximumFractionDigits: 1 })} <span>tCO₂e</span></div>
          </div>
          <div className="sbti-kpi">
            <div className="sbti-kpi__l">Alcance 3 total</div>
            <div className="sbti-kpi__v">{(calc.scope3Total || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })} <span>tCO₂e</span></div>
          </div>
          <div className={`sbti-kpi ${inv.inventoryYear && !inventoryRecent ? "sbti-kpi--alert" : ""}`}>
            <div className="sbti-kpi__l">Inventario vigente</div>
            <div className="sbti-kpi__v">{inv.inventoryYear ? (inventoryRecent ? "✔ Sí" : "✘ Vencido") : "—"}</div>
          </div>
        </div>

        <Form>
          <Form.Field>
            <label>Notas del inventario</label>
            <TextArea rows={2} value={inv.notes || ""} onChange={(_, d) => setInv({ notes: d.value })} />
          </Form.Field>
        </Form>
        <Button basic onClick={addSite}>+ Agregar planta al desglose</Button>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">MULTI-PLANTA</span> Desglose por sitio (opcional, informativo)</h3>
        {(record.sites || []).length ? (
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Planta</Table.HeaderCell>
                <Table.HeaderCell>País</Table.HeaderCell>
                <Table.HeaderCell>A1 (tCO₂e)</Table.HeaderCell>
                <Table.HeaderCell>A2 {inv.scope2Approach} (tCO₂e)</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {(record.sites || []).map((s, ix) => (
                <Table.Row key={ix}>
                  <Table.Cell><Input value={s.freeName || ""} onChange={(_, d) => setSite(ix, { freeName: d.value })} /></Table.Cell>
                  <Table.Cell>
                    <Select
                      search
                      selection
                      allowAdditions
                      options={countryOptionsWithCurrent(s.country)}
                      value={s.country || ""}
                      placeholder="País"
                      noResultsMessage="Escribí para agregarlo"
                      onAddItem={(_, d) => setSite(ix, { country: d.value })}
                      onChange={(_, d) => setSite(ix, { country: d.value })}
                    />
                  </Table.Cell>
                  <Table.Cell><Input type="number" step="0.01" value={s.scope1 || ""} onChange={(_, d) => setSite(ix, { scope1: d.value })} /></Table.Cell>
                  <Table.Cell><Input type="number" step="0.01" value={s.scope2 || ""} onChange={(_, d) => setSite(ix, { scope2: d.value })} /></Table.Cell>
                  <Table.Cell><Button icon="trash" basic size="tiny" onClick={() => removeSite(ix)} /></Table.Cell>
                </Table.Row>
              ))}
              <Table.Row>
                <Table.Cell><b>Suma sitios</b></Table.Cell>
                <Table.Cell />
                <Table.Cell>
                  <b>{(record.sites || []).reduce((a, s) => a + (parseFloat(String(s.scope1).replace(",", ".")) || 0), 0).toLocaleString("es-AR")}</b>
                </Table.Cell>
                <Table.Cell>
                  <b>{(record.sites || []).reduce((a, s) => a + (parseFloat(String(s.scope2).replace(",", ".")) || 0), 0).toLocaleString("es-AR")}</b>
                </Table.Cell>
                <Table.Cell><Button size="tiny" onClick={useSitesSum}>Usar como consolidado</Button></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        ) : (
          <p className="sbti-hint">
            Sin plantas cargadas. El desglose sirve para trazar cómo se consolida el objetivo de grupo a partir de
            cada sitio gestionado en Sustemia.
          </p>
        )}
      </div>

      <div className="sbti-card">
        <h3>Conformidad del inventario (requisitos de elegibilidad)</h3>
        <Form>
          <Form.Field>
            <Form.Checkbox
              label="Inventario conforme a GHG Protocol Corporate Standard + Scope 2 Guidance + Scope 3 Standard"
              checked={!!inv.ghgProtocolCompliant}
              onChange={(_, d) => setInv({ ghgProtocolCompliant: d.checked })}
            />
          </Form.Field>
          <Form.Field>
            <Form.Checkbox
              label="Cubre los 7 gases relevantes del GHG Protocol (CO₂, CH₄, N₂O, HFCs, PFCs, SF₆, NF₃)"
              checked={!!inv.sevenGases}
              onChange={(_, d) => setInv({ sevenGases: d.checked })}
            />
          </Form.Field>
          <Form.Field>
            <Form.Checkbox
              label="Bioenergía: emisiones y remociones de biomasa/biocombustibles reportadas junto al inventario e incluidas en el límite del objetivo"
              checked={!!inv.bioenergy}
              onChange={(_, d) => setInv({ bioenergy: d.checked })}
            />
          </Form.Field>
        </Form>
        <div className="sbti-src">
          <b>Fuentes:</b> Criterios Near-Term V5.3.1 (inventario, gases, bioenergía, exclusión ≤5%); Criteria
          Assessment Indicators v1.7 (abr-2026): inventario más reciente ≤2 años, sin datos proxy, A1 y A2 mismo
          año base. Guía sectorial 2019 §3.1.
        </div>
      </div>
    </div>
  );
}
